import { useEffect, useState } from "react"

import { useQuery } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { isRetiredImagerySource } from "./retired-imagery"

/**
 * - `checking`: the Design or its Site imagery is still loading.
 * - `not-retired`: the Design does not display retired imagery; nothing to guard.
 * - `verifying`: the saved retired image is being loaded; editing waits for the result.
 * - `available`: the saved retired image loads.
 * - `unavailable`: the saved retired image is missing or unreadable.
 */
export type RetiredImageryStatus =
  | `available`
  | `checking`
  | `not-retired`
  | `unavailable`
  | `verifying`

/** Whether layout editing must wait (`verifying`) or stop (`unavailable`). */
export const isEditingPaused = (status: RetiredImageryStatus): boolean =>
  status === `unavailable` || status === `verifying`

const LOAD_ATTEMPTS = 3
const LOAD_TIMEOUT_MS = 15_000
const RETRY_DELAY_MS = 1_000

const loadOnce = (url: string): Promise<boolean> =>
  new Promise((resolve) => {
    const image = new Image()
    const timer = window.setTimeout(() => {
      image.src = ``
      resolve(false)
    }, LOAD_TIMEOUT_MS)
    image.onload = (): void => {
      window.clearTimeout(timer)
      resolve(true)
    }
    image.onerror = (): void => {
      window.clearTimeout(timer)
      resolve(false)
    }
    image.src = url
  })

/**
 * Resolves whether the stored file loads. A few spaced attempts keep a brief network or storage
 * error from sending the user to a destructive image switch; a missing file is an expected
 * outcome, not an error.
 */
const canLoadImage = async (url: string): Promise<boolean> => {
  for (let attempt = 1; attempt <= LOAD_ATTEMPTS; attempt++) {
    if (await loadOnce(url)) return true
    if (attempt < LOAD_ATTEMPTS) {
      await new Promise((resolve) => window.setTimeout(resolve, RETRY_DELAY_MS))
    }
  }

  return false
}

/**
 * Reports whether a Design's saved retired (Bing) image can still be shown. Nothing here
 * requests new imagery; it only loads the stored file the Design already references.
 *
 * Once that image is unavailable the Design is in recovery: the guard then follows whichever
 * supported replacement the user selects, and editing resumes only after that image loads.
 */
export const useRetiredImageryStatus = ({
  source,
  imageUrl,
  ready,
  isAurora,
}: {
  /** The Design's selected imagery provider. */
  source: string | null | undefined
  /** The stored image for that provider, or null when the Site has none. */
  imageUrl: string | null | undefined
  /** False until both the Design and its Site imagery have loaded. */
  ready: boolean
  /** Aurora Designs render Aurora's screenshot, so Aurora's Bing metadata needs no saved image. */
  isAurora: boolean
}): {
  status: RetiredImageryStatus
  /** True when the unavailable image is the replacement chosen during recovery. */
  isReplacement: boolean
  retry: () => void
} => {
  const [isRecovering, setIsRecovering] = useState(false)
  const isRetiredSource = isRetiredImagerySource(source)
  const isGuarded = ready && !isAurora && (isRetiredSource || isRecovering)
  const { data: canLoad, refetch } = useQuery({
    queryKey: queryKeys.imagery.savedImageAvailability(imageUrl),
    queryFn: () => canLoadImage(imageUrl ?? ``),
    enabled: isGuarded && Boolean(imageUrl),
    staleTime: Infinity,
    cacheTime: Infinity,
  })
  // The designer already rendered the failed image; reload so it loads the file afresh.
  const retry = (): void => {
    void refetch().then(({ data }) => {
      if (data) window.location.reload()
    })
  }

  // A Bing Design can load before its Site imagery does; it waits paused rather than editable.
  const status: RetiredImageryStatus = !ready
    ? isRetiredSource && !isAurora
      ? `verifying`
      : `checking`
    : !isGuarded
      ? `not-retired`
      : !imageUrl
        ? `unavailable`
        : canLoad === undefined
          ? `verifying`
          : canLoad
            ? `available`
            : `unavailable`

  useEffect(() => {
    if (status === `unavailable` && isRetiredSource) setIsRecovering(true)
    if (status === `available` && !isRetiredSource) setIsRecovering(false)
  }, [status, isRetiredSource])

  return { status, isReplacement: isRecovering && !isRetiredSource, retry }
}
